import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import {
  useCmsThemeEditorOnColorsChange,
  useCmsThemeEditorPalette,
} from "@/context/cms/CmsThemeEditorContext";
import { useEnsurePaletteColor } from "@/hooks/useEnsurePaletteColor";
import {
  CMS_FONT_SIZES_PX,
  CMS_FORM_SELECT_TRIGGER_CLASS,
  CMS_GOOGLE_FONTS,
  getCmsFontVariants,
  resolveCmsFontVariant,
} from "@/lib/cms/cms-theme.constants";
import {
  TOOLBAR_CONTROL_CLASS,
  TOOLBAR_ICON_CLASS,
  TOOLBAR_SELECT_TRIGGER_CLASS,
} from "@/components/tiptap/toolbar.constants";
import { cn } from "@/lib/utils";
import type { Editor } from "@tiptap/react";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  ChevronDown,
  Heading2,
  Heading3,
  Type,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";

export function ToolbarGroup({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex shrink-0 items-center gap-1", className)}>
      {children}
    </div>
  );
}

export function ToolbarFontFamilySelect({ editor }: { editor: Editor }) {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const fontFamilyLabel = vt("bo.tiptap.fontFamily", "Police");
  const current =
    editor.getAttributes("textStyle").fontFamily ??
    CMS_GOOGLE_FONTS[0].googleFontFamily;
  const currentWeight = editor.getAttributes("textStyle").fontWeight as
    | number
    | undefined;
  const currentVariant = resolveCmsFontVariant(
    current,
    getCmsFontVariants(current).find((variant) => variant.weight === currentWeight)?.id,
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={fontFamilyLabel}
          title={fontFamilyLabel}
          className={cn(
            TOOLBAR_CONTROL_CLASS,
            "w-[170px] justify-between gap-2 px-3",
            CMS_FORM_SELECT_TRIGGER_CLASS,
          )}
        >
          <span className="truncate text-left">
            {current} {currentVariant.label}
          </span>
          <ChevronDown className="h-3 w-3 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[320px] w-[260px] overflow-y-auto">
        <DropdownMenuItem
          onClick={() => editor.chain().focus().unsetFontFamily().run()}
          className="cursor-pointer data-[highlighted]:text-white"
        >
          {t("cms.typography.none")}
        </DropdownMenuItem>
        {CMS_GOOGLE_FONTS.map((font) => (
          <DropdownMenuSub key={font.id}>
            <DropdownMenuSubTrigger
              className={cn(
                "cursor-pointer data-[highlighted]:text-white data-[state=open]:text-white",
                current === font.googleFontFamily && "bg-accent text-accent-foreground",
              )}
              style={{ fontFamily: `'${font.googleFontFamily}', sans-serif` }}
            >
              {font.label}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-[280px] overflow-y-auto">
              {font.variants.map((variant) => (
                <DropdownMenuItem
                  key={`${font.id}-${variant.id}`}
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .setFontVariant(font.googleFontFamily, variant.id)
                      .run()
                  }
                  className={cn(
                    "cursor-pointer data-[highlighted]:text-white",
                    current === font.googleFontFamily &&
                      currentVariant.id === variant.id &&
                      "bg-accent text-accent-foreground",
                  )}
                >
                  <span
                    style={{
                      fontFamily: `'${font.googleFontFamily}', sans-serif`,
                      fontWeight: variant.weight,
                    }}
                  >
                    {variant.label}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ToolbarFontSizeSelect({ editor }: { editor: Editor }) {
  const vt = useVisibleT();
  const fontSizeLabel = vt("bo.tiptap.fontSize", "Taille de la police");
  const fontSize = editor.getAttributes("textStyle").fontSize || "16px";

  return (
    <Select
      value={fontSize}
      onValueChange={(value) => {
        // Radix réémet onValueChange via son <select> caché (BubbleSelect) dès que
        // la valeur contrôlée change — y compris quand elle change à cause de
        // l'éditeur lui-même (chargement du contenu au montage d'un onglet). Au
        // montage, les <option> natives ne sont pas encore enregistrées, donc cet
        // « écho » arrive avec une valeur vide ; il peut aussi arriver avec la
        // valeur déjà active. Dans les deux cas ce n'est PAS une action de
        // l'utilisateur : on l'ignore, sinon le .focus() volerait le focus de la
        // page (scroll intempestif, effet d'ancre) et setFontSize("") corromprait
        // la taille de police du contenu. Un vrai clic passe toujours une taille
        // valide différente de l'état courant.
        const current = editor.getAttributes("textStyle").fontSize || "16px";
        if (!value || value === current) return;
        const sizeNumber = parseInt(value.replace("px", ""), 10);
        editor.chain().focus().setFontSize(value).run();
        if (Number.isFinite(sizeNumber) && sizeNumber > 0) {
          editor.chain().focus().setLineHeight(`${sizeNumber * 1.2}px`).run();
        }
      }}
    >
      <SelectTrigger
        className={cn("w-[80px]", TOOLBAR_SELECT_TRIGGER_CLASS)}
        aria-label={fontSizeLabel}
        title={fontSizeLabel}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {CMS_FONT_SIZES_PX.map((size) => (
          <SelectItem key={size} value={size}>
            {size}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ToolbarColorSelect({ editor }: { editor: Editor }) {
  const vt = useVisibleT();
  const colorLabel = vt("bo.tiptap.fontColor", "Couleur de la police");
  const palette = useCmsThemeEditorPalette();
  const onColorsChange = useCmsThemeEditorOnColorsChange();
  const { ensurePaletteColor } = useEnsurePaletteColor();
  const currentColor = editor.getAttributes("textStyle").color;

  const applyColor = async (hex: string) => {
    editor.chain().focus().setFontColor(hex).run();
    if (!palette.some((c) => c.hex.toUpperCase() === hex.toUpperCase())) {
      try {
        const { colors } = await ensurePaletteColor(hex);
        onColorsChange?.(colors);
      } catch {
        /* keep inline color in editor */
      }
    }
  };

  if (!palette.length) {
    return (
      <label className="inline-flex items-center gap-1" title={colorLabel}>
        <input
          type="color"
          className="h-9 w-11 cursor-pointer rounded border border-slate-200 bg-transparent"
          value={currentColor ?? "#171717"}
          onChange={(e) => void applyColor(e.target.value)}
          aria-label={colorLabel}
          title={colorLabel}
        />
        <Type className={TOOLBAR_ICON_CLASS + " text-slate-600"} />
      </label>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={colorLabel}
          title={colorLabel}
          className={cn(TOOLBAR_CONTROL_CLASS, "gap-1 px-2", CMS_FORM_SELECT_TRIGGER_CLASS)}
        >
          <span
            className="h-[18px] w-[18px] rounded border border-slate-200"
            style={{ backgroundColor: currentColor ?? palette[0]?.hex }}
          />
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="grid grid-cols-4 gap-1 p-2">
        {palette.map((color) => (
          <DropdownMenuItem
            key={color.id}
            className="p-1 justify-center"
            onClick={() => editor.chain().focus().setFontColor(color.hex).run()}
          >
            <span
              className="h-6 w-6 rounded border"
              style={{ backgroundColor: color.hex }}
              title={color.name}
            />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const PARAGRAPH_SPACING_OPTIONS = ["2", "4", "6"] as const;

function getParagraphSpacingValue(editor: Editor): string {
  const paragraphClass = editor.getAttributes("paragraph").class as
    | string
    | undefined;
  if (paragraphClass) {
    const match = paragraphClass.match(/spacing-paragraph-(\d+)/);
    if (match?.[1]) return match[1];
  }
  return "4";
}

export function ToolbarTextAlignGroup({ editor }: { editor: Editor }) {
  const vt = useVisibleT();
  const alignments = [
    { value: "left" as const, icon: AlignLeft, label: vt("bo.tiptap.alignLeft", "Aligner à gauche") },
    { value: "center" as const, icon: AlignCenter, label: vt("bo.tiptap.alignCenter", "Centrer") },
    { value: "right" as const, icon: AlignRight, label: vt("bo.tiptap.alignRight", "Aligner à droite") },
    {
      value: "justify" as const,
      icon: AlignJustify,
      label: vt("bo.tiptap.alignJustify", "Justifier"),
    },
  ];

  return (
    <>
      {alignments.map(({ value, icon: Icon, label }) => (
        <Toggle
          key={value}
          size="default"
          className={cn(TOOLBAR_CONTROL_CLASS, "w-9 px-0")}
          pressed={editor.isActive({ textAlign: value })}
          aria-label={label}
          title={label}
          onPressedChange={() =>
            editor.chain().focus().setTextAlign(value).run()
          }
        >
          <Icon className={TOOLBAR_ICON_CLASS} />
        </Toggle>
      ))}
    </>
  );
}

export function ToolbarHeadingToggles({
  editor,
  controlClassName = TOOLBAR_CONTROL_CLASS,
  iconClassName = TOOLBAR_ICON_CLASS,
}: {
  editor: Editor;
  controlClassName?: string;
  iconClassName?: string;
}) {
  const vt = useVisibleT();
  return (
    <>
      <Toggle
        size="default"
        className={controlClassName}
        pressed={editor.isActive("heading", { level: 2 })}
        aria-label={vt("bo.tiptap.headingH2", "Titre H2")}
        title={vt("bo.tiptap.headingH2", "Titre H2")}
        onPressedChange={() =>
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        }
      >
        <Heading2 className={iconClassName} />
      </Toggle>
      <Toggle
        size="default"
        className={controlClassName}
        pressed={editor.isActive("heading", { level: 3 })}
        aria-label={vt("bo.tiptap.headingH3", "Titre H3")}
        title={vt("bo.tiptap.headingH3", "Titre H3")}
        onPressedChange={() =>
          editor.chain().focus().toggleHeading({ level: 3 }).run()
        }
      >
        <Heading3 className={iconClassName} />
      </Toggle>
    </>
  );
}

export function ToolbarParagraphSpacingSelect({ editor }: { editor: Editor }) {
  const vt = useVisibleT();
  const spacingLabel = vt("bo.tiptap.spacing", "Espacement");
  const spacing = getParagraphSpacingValue(editor);

  return (
    <Select
      value={spacing}
      onValueChange={(value) => {
        // Cf. ToolbarFontSizeSelect : on ignore l'écho programmatique de Radix
        // (valeur vide au montage, ou valeur déjà active) pour éviter que .focus()
        // ne vole le focus de la page.
        if (!value || value === getParagraphSpacingValue(editor)) return;
        editor
          .chain()
          .focus()
          .updateAttributes("paragraph", {
            class: `spacing-paragraph-${value}`,
          })
          .run();
      }}
    >
      <SelectTrigger
        className={cn("w-[150px]", TOOLBAR_SELECT_TRIGGER_CLASS)}
        aria-label={spacingLabel}
        title={spacingLabel}
      >
        <SelectValue placeholder={spacingLabel}>
          {spacingLabel} {spacing}px
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {PARAGRAPH_SPACING_OPTIONS.map((option) => (
          <SelectItem key={option} value={option}>
            {vt("bo.tiptap.spacing", "Espacement")} {option}px
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** @deprecated Use ToolbarTextAlignGroup + ToolbarHeadingToggles + ToolbarParagraphSpacingSelect */
export function ToolbarLayoutMenu({ editor }: { editor: Editor }) {
  return (
    <>
      <ToolbarTextAlignGroup editor={editor} />
      <ToolbarHeadingToggles editor={editor} />
      <ToolbarParagraphSpacingSelect editor={editor} />
    </>
  );
}
